/**
 * View similar (sold-out sizes)
 * ---------------------------------------------------------------------------
 * When a size is sold out in the variant picker, snippets/variant-picker.liquid
 * renders a `.view-similar-trigger` button next to it. Clicking it opens the
 * shared <x-modal id="view-similar-modal"> (defined in sections/main-product.liquid)
 * and fills it with other products that have THAT size in stock.
 *
 * MATCHING
 * --------
 * Candidates are gathered from the most specific source first and then SCORED, so
 * "similar" means genuinely similar rather than "shares a product type":
 *
 *   Tier 1  the smallest curated collections this product is in (flannel-shirts,
 *           bowling-shirts, classic-t-shirts, the-8-bit-collection ...)
 *   Tier 2  a field-scoped search — product_type:"Bowling Shirt", tag:Flannel —
 *           which returns exact matches with no keyword noise
 *   Tier 3  Shopify's own related recommendations
 *
 * Everything that comes back is scored on shared signals: the custom.stye
 * ("Style") metafield, shared collections, shared tags, same product type. Only
 * candidates scoring above MIN_SCORE are shown as similar — a low scorer is
 * dropped rather than padded in, which is what made the old results feel random.
 *
 * Anything still missing is topped up from the Best Sellers collection and shown
 * in a SEPARATE, clearly labelled group so it never reads as "similar".
 *
 * Results come from the Section Rendering API (sections=view-similar), which
 * returns product-card markup annotated with each product's in-stock sizes and
 * similarity signals — facet params are ignored through ?sections=, so the size
 * filtering and the scoring both happen here on the client.
 */
(function () {
  'use strict';

  var themeSettings = (window.themeVariables && window.themeVariables.settings) || {};
  if (themeSettings.pageType && themeSettings.pageType !== 'product') {
    return;
  }

  var SECTION = 'view-similar';
  var TARGET_COUNT = 8;

  // Scoring weights. A candidate must reach MIN_SCORE to count as "similar" at all;
  // sharing only a broad product type ("Shirts & Tops") deliberately falls short.
  var SCORE = {
    style: 6, // shared custom.stye value — Minimalist, Bowling Shirts, Oversized ...
    collection: 4, // shared curated collection
    tag: 4, // shared style tag — Flannel, Earth, Paris ...
    type: 2 // same product type
  };
  // Any ONE real style signal qualifies; sharing only a product type ("Shirts & Tops",
  // 97 products) deliberately does not.
  var MIN_SCORE = 4;

  // Increments on every trigger click so a slow fetch can't overwrite a newer one.
  var requestToken = 0;

  // Memoize results per size so hover-prefetch + repeat opens are instant.
  var resultCache = Object.create(null);
  function getResults(data, size) {
    var key = String(size).toLowerCase();
    if (!resultCache[key]) {
      resultCache[key] = loadResults(data, size);
    }
    return resultCache[key];
  }

  function routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function getProductData() {
    var el = document.getElementById('view-similar-data');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      return null;
    }
  }

  function getModal() {
    return document.getElementById('view-similar-modal');
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  // Field-scoped search. `product_type:"Bowling Shirt"` returns exactly the bowling shirts,
  // where a bare keyword search leaks unrelated products in on title matches. Style tags are
  // OR-ed on so a flannel prefers other flannels within its type.
  function buildSearchUrl(data) {
    if (!data.type) return null;
    var query = 'product_type:"' + String(data.type).replace(/"/g, '') + '"';
    var tags = styleTagsOf(data);
    if (tags.length) {
      query =
        '(' +
        query +
        ') OR (' +
        tags
          .map(function (t) {
            return 'tag:"' + String(t).replace(/"/g, '') + '"';
          })
          .join(' OR ') +
        ')';
    }
    var params = new URLSearchParams();
    params.set('q', query);
    params.set('type', 'product');
    params.set('options[unavailable_products]', 'hide');
    params.set('sections', SECTION);
    return routeRoot() + 'search?' + params.toString();
  }

  function buildRecommendationsUrl(data) {
    if (!data.id) return null;
    var params = new URLSearchParams();
    params.set('product_id', String(data.id));
    params.set('intent', 'related');
    params.set('limit', '10');
    params.set('sections', SECTION);
    return routeRoot() + 'recommendations/products?' + params.toString();
  }

  // Collections are fetched best-selling first, which IS honoured through ?sections=.
  function buildCollectionUrl(handle) {
    if (!handle) return null;
    var params = new URLSearchParams();
    params.set('sort_by', 'best-selling');
    params.set('sections', SECTION);
    return routeRoot() + 'collections/' + encodeURIComponent(handle) + '?' + params.toString();
  }

  // ---- Similarity scoring -------------------------------------------------
  // Signals arrive pipe-wrapped ("|minimalist|oversized|") on both sides so a plain
  // indexOf is an exact whole-value match, never a partial one.
  function splitKey(value) {
    return String(value || '')
      .toLowerCase()
      .split('|')
      .filter(Boolean);
  }

  // Style tags are the ones worth searching on — season/campaign codes (SS26, BF25) are not.
  // Anything the merchant lists on the source product that also appears on a candidate scores;
  // for the search query we only use the first few so the URL stays sane.
  function styleTagsOf(data) {
    return splitKey(data.tags).slice(0, 3);
  }

  function scoreCard(card, source) {
    var score = 0;
    var style = card.getAttribute('data-vs-style') || '';
    var colls = card.getAttribute('data-vs-colls') || '';
    var tags = card.getAttribute('data-vs-tags') || '';
    var type = (card.getAttribute('data-vs-type') || '').toLowerCase();

    source.styleList.forEach(function (v) {
      if (style.indexOf('|' + v + '|') !== -1) score += SCORE.style;
    });
    source.collList.forEach(function (v) {
      if (colls.indexOf('|' + v + '|') !== -1) score += SCORE.collection;
    });
    source.tagList.forEach(function (v) {
      if (tags.indexOf('|' + v + '|') !== -1) score += SCORE.tag;
    });
    if (type && source.type && type === source.type) score += SCORE.type;

    return score;
  }

  function sourceSignals(data) {
    return {
      type: String(data.type || '').toLowerCase(),
      styleList: splitKey(data.style),
      tagList: splitKey(data.tags),
      collList: (data.styleCollections || []).map(function (h) { return String(h).toLowerCase(); })
    };
  }

  // Fetch a section render and return { handle, node } for each card that has `size` in stock.
  function fetchCards(url, size, excludeHandle) {
    var needle = '|' + String(size).toLowerCase() + '|';

    return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (res) {
        if (!res || !res.ok) return null;
        return res.json();
      })
      .then(function (data) {
        var html = data && data[SECTION];
        if (!html) return [];
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var out = [];
        doc.querySelectorAll('.vs-card').forEach(function (card) {
          var handle = card.getAttribute('data-product-handle');
          var sizes = card.getAttribute('data-available-sizes') || '';
          if (excludeHandle && handle === excludeHandle) return;
          if (sizes.indexOf(needle) === -1) return;
          out.push({ handle: handle, node: card });
        });
        return out;
      })
      .catch(function () {
        return [];
      });
  }

  function dedupeByHandle(items) {
    var seen = Object.create(null);
    var out = [];
    items.forEach(function (item) {
      if (item.handle) {
        if (seen[item.handle]) return;
        seen[item.handle] = true;
      }
      out.push(item);
    });
    return out;
  }

  function loadResults(data, size) {
    var target = data.target || TARGET_COUNT;
    var source = sourceSignals(data);

    // The similar sources are fetched in PARALLEL: the two smallest curated collections this
    // product belongs to, a field-scoped search, and Shopify's related recommendations.
    var urls = [];
    (data.styleCollections || []).slice(0, 2).forEach(function (handle) {
      urls.push(buildCollectionUrl(handle));
    });
    urls.push(buildSearchUrl(data));
    urls.push(buildRecommendationsUrl(data));

    var similarPromise = Promise.all(
      urls.filter(Boolean).map(function (url) {
        return fetchCards(url, size, data.handle);
      })
    ).then(function (lists) {
      var collected = [];
      lists.forEach(function (cards) {
        collected = collected.concat(cards);
      });

      // Score, drop anything that is not actually similar, best match first.
      return dedupeByHandle(collected)
        .map(function (item) {
          item.score = scoreCard(item.node, source);
          return item;
        })
        .filter(function (item) {
          return item.score >= MIN_SCORE;
        })
        .sort(function (a, b) {
          return b.score - a.score;
        })
        .slice(0, target);
    });

    return similarPromise.then(function (similar) {
      if (similar.length >= target || data.fillWithBestSellers === false) {
        return { similar: similar, bestSellers: [] };
      }

      // Top up from Best Sellers — kept in its own group, never merged into "similar".
      var bestUrl = buildCollectionUrl(data.bestSellers || 'best-sellers');
      if (!bestUrl) return { similar: similar, bestSellers: [] };

      return fetchCards(bestUrl, size, data.handle).then(function (best) {
        var seen = Object.create(null);
        similar.forEach(function (item) {
          if (item.handle) seen[item.handle] = true;
        });
        var filler = dedupeByHandle(best).filter(function (item) {
          return !item.handle || !seen[item.handle];
        });
        return { similar: similar, bestSellers: filler.slice(0, target - similar.length) };
      });
    });
  }

  function renderLoading(modal) {
    var body = modal.querySelector('.view-similar__body');
    if (body) {
      body.innerHTML =
        '<div class="view-similar__loading"><div class="view-similar__spinner" role="status" aria-label="Loading"></div></div>';
    }
  }

  // One labelled group of cards. `note` is the disclaimer under the heading; a group that is
  // supposed to carry one is NOT rendered without it, so "Best Sellers" can never appear
  // unlabelled and be mistaken for "similar".
  function buildGroup(items, size, heading, note, modifier, titleLines) {
    if (!items.length) return null;
    if (note === null) return null;

    var group = document.createElement('section');
    group.className = 'view-similar__group' + (modifier ? ' view-similar__group--' + modifier : '');

    if (heading) {
      var h = document.createElement('p');
      h.className = 'view-similar__group-title h6';
      h.textContent = heading;
      group.appendChild(h);
    }

    if (note) {
      var n = document.createElement('p');
      n.className = 'view-similar__group-note text-subdued text-sm';
      n.textContent = note;
      group.appendChild(n);
    }

    var list = document.createElement('product-list');
    list.className = 'product-list view-similar__list';
    list.style.setProperty('--vs-title-lines', titleLines || 2);
    items.forEach(function (item) {
      wireCard(item.node, size);
      list.appendChild(item.node);
    });
    group.appendChild(list);
    return group;
  }

  function renderResults(modal, size, typeLabel, results, data) {
    var body = modal.querySelector('.view-similar__body');
    if (!body) return;

    var text = (data && data.text) || {};
    var titleLines = (data && data.titleLines) || 2;
    var similar = results.similar || [];
    var bestSellers = results.bestSellers || [];

    // The modal heading is written before the fetch resolves, so it has to be corrected here:
    // with nothing genuinely similar to show, it must not keep claiming "Similar <type>".
    var modalTitle = modal.querySelector('[data-vs-modal-title]');
    var subTitle = modal.querySelector('.view-similar__title');
    if (similar.length) {
      if (modalTitle) modalTitle.textContent = 'Similar ' + typeLabel;
      if (subTitle) subTitle.textContent = 'Other ' + typeLabel + ' available in size ' + size;
    } else {
      if (modalTitle) modalTitle.textContent = 'Available in size ' + size;
      if (subTitle) {
        subTitle.textContent = bestSellers.length
          ? "We couldn't find a close match in size " + size + '.'
          : '';
      }
    }

    if (!similar.length && !bestSellers.length) {
      // Say what is actually true: nothing scored as a close match. Claiming nothing is in
      // stock would be a stronger statement than the data supports.
      body.innerHTML =
        '<p class="view-similar__empty">We couldn\'t find a close match in size ' +
        escapeHtml(size) +
        ' right now. Please check back soon.</p>';
      return;
    }

    body.innerHTML = '';

    // Only label the similar group when Best Sellers sit alongside it — on its own the
    // modal heading already says what it is, and a lone heading just adds noise.
    var similarHeading = bestSellers.length ? text.similarHeading || 'Similar styles' : '';
    var similarGroup = buildGroup(similar, size, similarHeading, '', 'similar', titleLines);
    if (similarGroup) body.appendChild(similarGroup);

    var bestNote = text.bestSellersNote;
    var bestHeading = text.bestSellersHeading;
    var bestGroup = buildGroup(
      bestSellers,
      size,
      bestHeading,
      bestHeading && bestNote ? bestNote : null,
      'bestsellers',
      titleLines
    );
    if (bestGroup) body.appendChild(bestGroup);
  }

  // Wire the per-card "Add to cart": locked to the sold-out size. If the size is in stock in
  // more than one colour, the button opens a small colour popup; otherwise it adds directly.
  function wireCard(card, size) {
    // Results are cached per size and the cache holds live DOM nodes, so reopening the same
    // size hands back cards that are already wired. Without this guard each reopen stacks
    // another click listener on the colour popup.
    if (card.dataset.vsWired === '1') return;
    card.dataset.vsWired = '1';

    var form = card.querySelector('product-form');
    var idInput = card.querySelector('[data-vs-id]');
    var button = card.querySelector('[data-vs-add]');
    if (!form || !idInput || !button) return;

    var variants = [];
    try {
      variants = JSON.parse(card.getAttribute('data-vs-variants') || '[]') || [];
    } catch (err) {
      variants = [];
    }

    var needle = String(size).toLowerCase();
    var matches = variants.filter(function (v) {
      return String(v.s).toLowerCase() === needle;
    });

    if (!matches.length) {
      form.hidden = true; // shouldn't happen (card only shown if size is in stock)
      return;
    }

    idInput.value = matches[0].id;

    // Distinct colours that have this size in stock
    var colours = [];
    var seen = Object.create(null);
    matches.forEach(function (m) {
      if (m.c && !seen[m.c]) {
        seen[m.c] = true;
        colours.push(m);
      }
    });

    if (colours.length > 1) {
      var swatches = {};
      try {
        swatches = JSON.parse(card.getAttribute('data-vs-swatches') || '{}') || {};
      } catch (err) {
        swatches = {};
      }
      button.type = 'button'; // don't submit yet — choose a colour first
      button.addEventListener('click', function (event) {
        event.preventDefault();
        openColorPopup(button, colours, swatches, idInput, form);
      });
    }
    // else: single colour / no colour option -> stays type="submit", adds matches[0] natively
  }

  // ---- Small colour popup (shown when a product has the size in multiple colours) ----
  var colorPopup = null;

  function hideColorPopup() {
    if (colorPopup) colorPopup.hidden = true;
  }

  function ensureColorPopup() {
    if (colorPopup) return colorPopup;
    colorPopup = document.createElement('div');
    colorPopup.className = 'vs-color-popup color-scheme color-scheme--dialog';
    colorPopup.hidden = true;
    document.body.appendChild(colorPopup);
    document.addEventListener(
      'click',
      function (event) {
        if (colorPopup.hidden) return;
        var t = event.target;
        if (t && t.closest && (t.closest('.vs-color-popup') || t.closest('[data-vs-add]'))) return;
        hideColorPopup();
      },
      true
    );
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') hideColorPopup();
    });
    window.addEventListener('scroll', hideColorPopup, true);
    window.addEventListener('resize', hideColorPopup);
    return colorPopup;
  }

  function submitAddForm(form) {
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
  }

  function openColorPopup(button, colours, swatches, idInput, form) {
    var popup = ensureColorPopup();
    popup.innerHTML = '';

    var heading = document.createElement('span');
    heading.className = 'vs-color-popup__label';
    heading.textContent = 'Choose colour';
    popup.appendChild(heading);

    var listWrap = document.createElement('div');
    listWrap.className = 'vs-color-popup__list';
    colours.forEach(function (m) {
      var opt = document.createElement('button');
      opt.type = 'button';
      opt.className = 'vs-color-popup__option';

      var block = document.createElement('span');
      block.className = 'vs-color-popup__swatch';
      var sw = swatches && swatches[m.c];
      if (sw && sw.image) {
        block.style.backgroundImage = 'url("' + sw.image + '")';
      } else if (sw && sw.color) {
        block.style.backgroundColor = sw.color;
      } else {
        block.style.backgroundColor = m.c; // fallback: colour name as CSS colour
      }
      opt.appendChild(block);

      var name = document.createElement('span');
      name.className = 'vs-color-popup__name';
      name.textContent = m.c;
      opt.appendChild(name);

      opt.addEventListener('click', function () {
        idInput.value = m.id;
        hideColorPopup();
        submitAddForm(form);
      });
      listWrap.appendChild(opt);
    });
    popup.appendChild(listWrap);

    popup.hidden = false;
    // Position near the button (fixed to the viewport; the popup is transient)
    var rect = button.getBoundingClientRect();
    var pw = popup.offsetWidth;
    var ph = popup.offsetHeight;
    var left = Math.min(Math.max(8, rect.left), window.innerWidth - pw - 8);
    var top = rect.top - ph - 8;
    if (top < 8) top = rect.bottom + 8;
    popup.style.left = left + 'px';
    popup.style.top = top + 'px';
  }

  function openModal(modal) {
    return Promise.resolve(
      window.customElements && window.customElements.whenDefined
        ? window.customElements.whenDefined('x-modal')
        : null
    ).then(function () {
      if (typeof modal.show === 'function') {
        modal.show();
      } else {
        modal.setAttribute('open', '');
      }
    });
  }

  function hideSizePopover(trigger) {
    var popover = trigger.closest('x-popover');
    if (popover && typeof popover.hide === 'function') {
      popover.hide();
    }
  }

  function handleTrigger(trigger) {
    var data = getProductData();
    var modal = getModal();
    if (!data || !modal) return;

    var size = trigger.getAttribute('data-vs-size');
    if (!size) return;

    var token = ++requestToken;
    var typeLabel = data.type || 'products';

    var titleEl = modal.querySelector('.view-similar__title');
    if (titleEl) titleEl.textContent = 'Other ' + typeLabel + ' available in size ' + size;
    renderLoading(modal);
    openModal(modal);
    hideSizePopover(trigger);

    getResults(data, size).then(function (results) {
      if (token !== requestToken) return; // a newer click superseded this one
      renderResults(modal, size, typeLabel, results, data);
    });
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    var trigger = target && target.closest ? target.closest('.view-similar-trigger') : null;
    if (!trigger) return;
    // Sold-out size buttons live inside the size <label>; stop the click from
    // toggling the (sold-out) radio or bubbling to the popover listeners.
    event.preventDefault();
    event.stopPropagation();
    handleTrigger(trigger);
  });

  // Warm the cache when the customer hovers/focuses a "View similar" link, so the click feels instant.
  function prefetchFromEvent(event) {
    var t = event.target;
    var trigger = t && t.closest ? t.closest('.view-similar-trigger') : null;
    if (!trigger) return;
    var data = getProductData();
    var size = trigger.getAttribute('data-vs-size');
    if (data && size) getResults(data, size);
  }
  document.addEventListener('mouseover', prefetchFromEvent);
  document.addEventListener('focusin', prefetchFromEvent);

  // When a card is added to cart, close our modal so the cart drawer isn't buried behind it.
  var vsModal = getModal();
  if (vsModal) {
    vsModal.addEventListener('variant:add', function () {
      if (typeof vsModal.hide === 'function') vsModal.hide();
    });
  }
})();
